import { Routes, Route, useLocation } from 'react-router-dom';
import { useEffect, useLayoutEffect, lazy, Suspense, type ComponentType } from 'react';
import CenteredNavbar from './components/CenteredNavbar';
import Footer from './components/Footer';
import PasswordProtection from './components/PasswordProtection';

// ── Page Loading ─────────────────────────────────────────────────────────────
// Client build: lazy imports via import.meta.glob for code-splitting.
// SSR build: eager-loaded pages are injected via __SSR_PAGES__ from entry-server.tsx
//            (keeps the eager glob out of this file to avoid Vite mixed-import warnings).

const lazyPages = import.meta.glob('./pages/**/*.tsx') as Record<string, () => Promise<{ default: ComponentType }>>;

// SSR pages are set by entry-server.tsx before render
declare global {
  var __SSR_PAGES__: Record<string, { default: ComponentType }> | undefined;
}

function loadPage(name: string): ComponentType {
  const key = `./pages/${name}.tsx`;
  // SSR: use eagerly-loaded pages from entry-server
  if (typeof globalThis.__SSR_PAGES__ !== 'undefined') {
    return globalThis.__SSR_PAGES__[key]?.default ?? (() => null);
  }
  // Client: lazy import for code-splitting
  return lazy(lazyPages[key]);
}

const HomePage = loadPage('HomePage');
const PropertiesPage = loadPage('PropertiesPage');
const PropertyDetailPage = loadPage('PropertyDetailPage');
const AboutPage = loadPage('AboutPage');
const TeamPage = loadPage('TeamPage');
const TeamMemberDetailPage = loadPage('TeamMemberDetailPage');
const ContactPage = loadPage('ContactPage');
const CareersPage = loadPage('CareersPage');
const NotFoundPage = loadPage('NotFoundPage');
const TestPage = loadPage('TestPage');
const LandlordRepresentationPage = loadPage('LandlordRepresentationPage');
const TenantRepresentationPage = loadPage('TenantRepresentationPage');
const InvestmentSalesPage = loadPage('InvestmentSalesPage');
const PrivacyPolicyPage = loadPage('PrivacyPolicyPage');
const TermsAndConditionsPage = loadPage('TermsAndConditionsPage');
const GreatReimagining = loadPage('articles/GreatReimagining');
const AutonomousBuildings = loadPage('articles/AutonomousBuildings');
const OfficeToResidential = loadPage('articles/OfficeToResidential');
const SmartToAutonomous = loadPage('articles/SmartToAutonomous');

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function ScrollToTop() {
  const { pathname } = useLocation();

  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    if (pathname === '/properties' && sessionStorage.getItem('propertiesPageScrollPosition')) {
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname]);

  return null;
}

export default function App() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // Dynamically import browser-only optimizers
    import('./utils/resourceOptimization').then(({ initializePerformanceOptimizations }) => {
      initializePerformanceOptimizations();
    });

    import('./utils/thirdPartyOptimizer').then(({ thirdPartyOptimizer }) => {
      thirdPartyOptimizer.init();
    });

    return () => {
      import('./utils/thirdPartyOptimizer').then(({ thirdPartyOptimizer }) => {
        thirdPartyOptimizer.destroy();
      });
    };
  }, []);

  return (
    <PasswordProtection>
      <ScrollToTop />
      <CenteredNavbar />
      <main id="main-content">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/properties" element={<PropertiesPage />} />
            <Route path="/properties/:property_slug" element={<PropertyDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/team/:slug" element={<TeamMemberDetailPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/services/landlord-representation" element={<LandlordRepresentationPage />} />
            <Route path="/services/tenant-representation" element={<TenantRepresentationPage />} />
            <Route path="/services/investment-sales" element={<InvestmentSalesPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
            <Route path="/the-great-reimagining" element={<GreatReimagining />} />
            <Route path="/from-smart-to-self-aware" element={<AutonomousBuildings />} />
            <Route path="/the-death-of-the-office-cube" element={<OfficeToResidential />} />
            <Route path="/the-great-leap-from-smart-to-autonomous" element={<SmartToAutonomous />} />
            <Route path="/test" element={<TestPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
       <Footer />
    </PasswordProtection>
  );
}
